import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { 
  Users, 
  Shield, 
  Search, 
  Loader2,
  UserCog,
  Mail,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Pencil,
  ArrowRight,
  ClipboardList,
  UserPlus,
  Eye,
  EyeOff
} from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full system access', color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'accounting', label: 'Accounting', description: 'Expenses, payments, bills', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'sales', label: 'Sales', description: 'CRM, quotes, orders, POs', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'production', label: 'Production', description: 'Work orders, order status', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'front_desk', label: 'Front Desk', description: 'Contact forms, basic access', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'user', label: 'User', description: 'Basic user access', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  { value: 'customer', label: 'Customer', description: 'Customer portal only', color: 'bg-green-100 text-green-800 border-green-200' },
];

// Roles that can be combined (staff roles)
const COMBINABLE_ROLES = ['accounting', 'sales', 'production', 'front_desk'];

export default function AdminRoleManagement({ onNavigateToAuditLog }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  
  // Create staff user state
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', roles: ['sales'] });
  const [showPassword, setShowPassword] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRole]);

  const fetchUsers = async (search = '') => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      
      const data = await adminAPI.getUsers(params);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    // Get user's roles (support both single and multiple)
    const userRoles = user.roles || [user.role || 'customer'];
    setSelectedRoles(userRoles);
    setShowModal(true);
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        // Don't allow removing the last role
        if (prev.length === 1) return prev;
        return prev.filter(r => r !== role);
      } else {
        // If selecting admin, make it the only role
        if (role === 'admin') {
          return ['admin'];
        }
        // If selecting customer, make it the only role
        if (role === 'customer') {
          return ['customer'];
        }
        // If selecting user, make it the only role
        if (role === 'user') {
          return ['user'];
        }
        // If currently admin/customer/user and selecting a staff role, switch
        if (prev.includes('admin') || prev.includes('customer') || prev.includes('user')) {
          return [role];
        }
        // Allow combining staff roles
        return [...prev, role];
      }
    });
  };

  // Show confirmation modal before saving
  const handleSaveClick = () => {
    if (!selectedUser || selectedRoles.length === 0) return;
    
    const currentRoles = getUserRoles(selectedUser);
    // Check if roles actually changed
    if (JSON.stringify([...currentRoles].sort()) === JSON.stringify([...selectedRoles].sort())) {
      setShowModal(false);
      return;
    }
    
    setShowModal(false);
    setShowConfirmModal(true);
  };

  const saveRoles = async () => {
    if (!selectedUser || selectedRoles.length === 0) return;
    
    try {
      setUpdating(true);
      await adminAPI.updateUserRoles(selectedUser.user_id, selectedRoles, changeReason || null, 'admin_ui');
      setShowConfirmModal(false);
      setShowModal(false);
      setSelectedUser(null);
      setChangeReason('');
      fetchUsers(searchQuery);
    } catch (err) {
      console.error('Failed to update roles:', err);
      alert('Failed to update roles: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const cancelConfirmation = () => {
    setShowConfirmModal(false);
    setShowModal(true);
    setChangeReason('');
  };

  // Create staff user
  const handleCreateStaff = async () => {
    setCreateError('');
    
    if (!newUser.name.trim()) {
      setCreateError('Name is required');
      return;
    }
    if (!newUser.email.trim()) {
      setCreateError('Email is required');
      return;
    }
    if (!newUser.password || newUser.password.length < 6) {
      setCreateError('Password must be at least 6 characters');
      return;
    }
    if (newUser.roles.length === 0) {
      setCreateError('At least one role is required');
      return;
    }
    
    try {
      setUpdating(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/create-staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create user');
      }
      
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', password: '', roles: ['sales'] });
      fetchUsers(searchQuery);
      alert(`Staff user "${newUser.name}" created successfully!`);
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const toggleNewUserRole = (role) => {
    setNewUser(prev => {
      const currentRoles = prev.roles;
      if (currentRoles.includes(role)) {
        if (currentRoles.length === 1) return prev;
        return { ...prev, roles: currentRoles.filter(r => r !== role) };
      }
      // Exclusive roles replace all others
      if (['admin', 'customer', 'user'].includes(role)) {
        return { ...prev, roles: [role] };
      }
      // Staff roles can combine, but replace exclusive roles
      if (currentRoles.some(r => ['admin', 'customer', 'user'].includes(r))) {
        return { ...prev, roles: [role] };
      }
      return { ...prev, roles: [...currentRoles, role] };
    });
  };

  const getUserRoles = (user) => {
    return user.roles || [user.role || 'customer'];
  };

  const getRoleStyle = (role) => {
    const found = ROLES.find(r => r.value === role);
    return found ? found.color : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getRoleLabel = (role) => {
    const found = ROLES.find(r => r.value === role);
    return found ? found.label : role;
  };

  // Stats - count users who have each role (in their roles array)
  const stats = ROLES.reduce((acc, role) => {
    acc[role.value] = users.filter(u => getUserRoles(u).includes(role.value)).length;
    return acc;
  }, {});

  return (
    <div className="p-6" data-testid="admin-role-management-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
          <p className="text-gray-600">Assign multiple roles to staff members</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCreateModal(true)}
            data-testid="create-staff-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create Staff User
          </Button>
          {onNavigateToAuditLog && (
            <Button 
              variant="outline" 
              onClick={onNavigateToAuditLog}
              data-testid="view-audit-log-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              View Audit Log
            </Button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Multi-Role Support</p>
            <p className="text-sm text-blue-700 mt-1">
              Staff members can have multiple roles (e.g., Sales + Production). Click &quot;Edit Roles&quot; to assign combinations.
              Admin, User, and Customer roles are exclusive and cannot be combined with other roles.
              All role changes are logged for audit purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Role Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {ROLES.map(role => (
          <div 
            key={role.value}
            className={`rounded-lg border p-3 cursor-pointer transition-all ${
              filterRole === role.value ? 'ring-2 ring-offset-1 ring-blue-500' : ''
            }`}
            onClick={() => setFilterRole(filterRole === role.value ? '' : role.value)}
            data-testid={`role-stat-${role.value}`}
          >
            <div className="flex items-center gap-2">
              <Badge className={`${role.color} border`}>{role.label}</Badge>
              <span className="text-lg font-bold">{stats[role.value] || 0}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{role.description}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="user-search-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="user-search-btn">
            Search
          </Button>
          {filterRole && (
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => { setFilterRole(''); fetchUsers(searchQuery); }}
            >
              Clear Filter
            </Button>
          )}
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">User</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Roles</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Provider</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Joined</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">Loading users...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <Users className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No users found</p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user.user_id} 
                    className="border-b hover:bg-gray-50 transition-colors"
                    data-testid={`user-row-${user.user_id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name || 'Unnamed'}</p>
                          <p className="text-xs text-gray-400 font-mono">{user.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-3.5 h-3.5" />
                        {user.email}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {getUserRoles(user).map(role => (
                          <Badge key={role} className={`${getRoleStyle(role)} border text-xs`}>
                            {getRoleLabel(role)}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 capitalize">
                      {user.provider || 'email'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openRoleModal(user)}
                        data-testid={`edit-roles-${user.user_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        Edit Roles
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
          Showing {users.length} of {total} users
          {filterRole && <span className="ml-2">• Filtered by: <Badge variant="outline">{getRoleLabel(filterRole)}</Badge></span>}
        </div>
      </div>

      {/* Role Permissions Info */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Role Permissions Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ROLES.map(role => (
            <div key={role.value} className={`border rounded-lg p-4 ${role.color.split(' ')[0]} bg-opacity-20`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${role.color} border`}>{role.label}</Badge>
                {COMBINABLE_ROLES.includes(role.value) && (
                  <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded">Combinable</span>
                )}
              </div>
              <p className="text-sm text-gray-600">{role.description}</p>
              <ul className="mt-2 text-xs text-gray-500 space-y-1">
                {role.value === 'admin' && (
                  <>
                    <li>• Full access to all features</li>
                    <li>• User management</li>
                    <li>• System configuration</li>
                  </>
                )}
                {role.value === 'sales' && (
                  <>
                    <li>• CRM read/write</li>
                    <li>• Quotes read/write</li>
                    <li>• Orders read/write</li>
                  </>
                )}
                {role.value === 'production' && (
                  <>
                    <li>• Work orders read/write</li>
                    <li>• Order status updates</li>
                    <li>• Artwork proofs</li>
                  </>
                )}
                {role.value === 'front_desk' && (
                  <>
                    <li>• Contact forms</li>
                    <li>• Basic order lookup</li>
                    <li>• Customer support</li>
                  </>
                )}
                {role.value === 'user' && (
                  <>
                    <li>• View own orders</li>
                    <li>• Basic account access</li>
                  </>
                )}
                {role.value === 'customer' && (
                  <>
                    <li>• Shop and checkout</li>
                    <li>• View order history</li>
                    <li>• Account settings</li>
                  </>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Roles Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="edit-roles-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-500" />
              Edit User Roles
            </DialogTitle>
            <DialogDescription>
              Select one or more roles for this user. Staff roles (Sales, Production, Front Desk) can be combined.
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{selectedUser.name || 'Unnamed'}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              
              {/* Role Selection */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Select Roles:</p>
                
                {/* Exclusive Roles */}
                <div className="border rounded-lg p-3 space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Exclusive Roles</p>
                  {ROLES.filter(r => !COMBINABLE_ROLES.includes(r.value)).map(role => (
                    <label 
                      key={role.value}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        selectedRoles.includes(role.value) ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedRoles.includes(role.value)}
                        onCheckedChange={() => toggleRole(role.value)}
                        data-testid={`role-checkbox-${role.value}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`${role.color} border text-xs`}>{role.label}</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                
                {/* Combinable Staff Roles */}
                <div className="border rounded-lg p-3 space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Staff Roles (Can Combine)</p>
                  {ROLES.filter(r => COMBINABLE_ROLES.includes(r.value)).map(role => (
                    <label 
                      key={role.value}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        selectedRoles.includes(role.value) ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox 
                        checked={selectedRoles.includes(role.value)}
                        onCheckedChange={() => toggleRole(role.value)}
                        data-testid={`role-checkbox-${role.value}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={`${role.color} border text-xs`}>{role.label}</Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>
                      </div>
                    </label>
                  ))}
                  {selectedRoles.some(r => ['admin', 'customer', 'user'].includes(r)) && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Click a staff role to switch from {selectedRoles.find(r => ['admin', 'customer', 'user'].includes(r))}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Selected Roles Preview */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 mb-2">Selected Roles:</p>
                <div className="flex flex-wrap gap-1">
                  {selectedRoles.map(role => (
                    <Badge key={role} className={`${getRoleStyle(role)} border`}>
                      {getRoleLabel(role)}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {selectedRoles.includes('admin') && (
                <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Admin users have full system access. Use caution.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowModal(false); setSelectedUser(null); }}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveClick}
              disabled={updating || selectedRoles.length === 0}
              data-testid="save-roles-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md" data-testid="confirm-role-change-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Role Change
            </DialogTitle>
            <DialogDescription>
              Review and confirm the role change below.
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{selectedUser.name || 'Unnamed'}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>

              {/* Role Change Summary */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-3">Role Change:</p>
                <div className="flex items-center gap-3">
                  <div className="flex flex-wrap gap-1">
                    {getUserRoles(selectedUser).map(role => (
                      <Badge key={role} className={`${getRoleStyle(role)} border text-xs`}>
                        {getRoleLabel(role)}
                      </Badge>
                    ))}
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div className="flex flex-wrap gap-1">
                    {selectedRoles.map(role => (
                      <Badge key={role} className={`${getRoleStyle(role)} border text-xs`}>
                        {getRoleLabel(role)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for change (optional)
                </label>
                <Textarea
                  placeholder="e.g., Promoted to Sales team, Temporary access for project..."
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  className="resize-none"
                  rows={3}
                  data-testid="role-change-reason-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be recorded in the audit log.
                </p>
              </div>

              {selectedRoles.includes('admin') && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Warning: This will grant full admin access to the system.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={cancelConfirmation}
              disabled={updating}
            >
              Back
            </Button>
            <Button 
              onClick={saveRoles}
              disabled={updating}
              data-testid="confirm-role-change-btn"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Change
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Staff User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md" data-testid="create-staff-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-500" />
              Create Staff User
            </DialogTitle>
            <DialogDescription>
              Create a new staff account with pre-verified access.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="staff-name">Full Name *</Label>
              <Input
                id="staff-name"
                placeholder="John Smith"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                data-testid="staff-name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="staff-email">Email *</Label>
              <Input
                id="staff-email"
                type="email"
                placeholder="john@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                data-testid="staff-email-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="staff-password">Password *</Label>
              <div className="relative">
                <Input
                  id="staff-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 6 characters"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  data-testid="staff-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <Label>Roles *</Label>
              <div className="border rounded-lg p-3 space-y-2">
                {ROLES.filter(r => r.value !== 'customer').map(role => (
                  <label
                    key={role.value}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      newUser.roles.includes(role.value) ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Checkbox
                      checked={newUser.roles.includes(role.value)}
                      onCheckedChange={() => toggleNewUserRole(role.value)}
                      data-testid={`new-role-${role.value}`}
                    />
                    <div className="flex-1">
                      <Badge className={`${role.color} border text-xs`}>{role.label}</Badge>
                      <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {createError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4" />
                {createError}
              </div>
            )}

            {/* Selected Roles Preview */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600 mb-2">Selected Roles:</p>
              <div className="flex flex-wrap gap-1">
                {newUser.roles.map(role => (
                  <Badge key={role} className={`${getRoleStyle(role)} border`}>
                    {getRoleLabel(role)}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewUser({ name: '', email: '', password: '', roles: ['sales'] });
                setCreateError('');
              }}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateStaff}
              disabled={updating}
              data-testid="create-staff-submit-btn"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
